import { lazy, Suspense, useMemo } from 'react';
import { fmt, isUnavailable } from './portfolioData';

/**
 * "Performance over time" — the period-over-period comparison.
 *
 * The design calls this "the one chart the category actually uses ... because
 * the overlay is the read, not the level", which is why it is a two-series
 * overlay rather than one line: solid current period, dashed prior period, one
 * metric at a time.
 *
 * ONE METRIC AT A TIME IS LOAD-BEARING, not a simplification. Cost and CTR do
 * not share a scale, and putting them on one plot would need a second y-axis —
 * the single most common way a chart like this misleads, because the crossing
 * point of two independently-scaled lines is an artifact of the two scales, not
 * a fact about the account. The chips switch the metric; the axis stays single.
 *
 * COLOURS are not the design's literal pair. It used #0b2b3b / #8ba6b8, and the
 * second fails a 3:1 contrast floor against white (2.48:1) — a dashed line at
 * that weight is the thinnest mark on the page and the first thing to disappear
 * on a projector or a poor screen. #6d8798 is the nearest step that clears it
 * (checked with the dataviz validator: contrast PASS, CVD separation ΔE 33.3
 * protan / 33.5 normal against the primary). Visually it reads the same.
 *
 * Identity is never carried by colour alone: the prior-period line is also
 * dashed, and both series are named with their totals in the legend below.
 *
 * The plot itself is lazy — see TrendPlot for why.
 */

const TrendPlot = lazy(() => import('./TrendPlot'));

export const CURRENT_COLOR = '#0b2b3b';
export const PRIOR_COLOR = '#6d8798';

export default function TrendChart({
  metrics, metric, onMetric, series, currency = null, windowLabel, newestPullAt = null,
  coverage = null, totalCampaigns = null, daysPerPeriod = null,
  compared = false, onToggleCompare = null, compareRequested = false,
  latestDayExcluded = null,
  totals = null,
}) {
  const active = metrics.find((m) => m.key === metric) || metrics[0];

  // Recharts wants a flat row per x value. Flattened here rather than in the
  // parent so the parent can hand over whatever the (future) series endpoint
  // returns without knowing the chart's shape.
  const data = useMemo(() => (Array.isArray(series) ? series.map((p) => ({
    date: p.date,
    current: p.current ?? null,
    previous: p.previous ?? null,
  })) : []), [series]);

  const totalsOf = (key) => {
    // V2 supplies ratios of summed counters, including CPA and CTR.
    if (totals) return totals[key] ?? null;
    const vals = data.map((d) => d[key]).filter((v) => v !== null && v !== undefined);
    if (!vals.length) return null;
    // A rate averaged is a rate; a count summed is a count. Getting this wrong
    // prints a CTR of 190% under a legend that looks authoritative.
    return active.rate
      ? vals.reduce((a, b) => a + b, 0) / vals.length
      : vals.reduce((a, b) => a + b, 0);
  };

  const chips = (
    <div className="mchips">
      {metrics.map((m) => (
        <button
          key={m.key}
          type="button"
          className={`mchip${m.key === active.key ? ' on' : ''}`}
          onClick={() => onMetric(m.key)}
          disabled={!data.length}
          aria-pressed={m.key === active.key}
        >
          {m.label}
        </button>
      ))}
      {/* Kept ENABLED when there is nothing to plot: turning the comparison off
          is one of the reasons a chart is empty, so the control that fixes it
          must still be reachable from the empty state. */}
      {onToggleCompare ? (
        <button
          type="button"
          className={`mchip cmpchip${compareRequested ? ' on' : ''}`}
          onClick={onToggleCompare}
          aria-pressed={compareRequested}
          title="Split the pull window in half and overlay the older half"
        >
          Compare periods
        </button>
      ) : null}
    </div>
  );

  if (!data.length) {
    const why = isUnavailable(series) ? series : null;
    return (
      <div className="card">
        <div className="ch">
          <h2>Performance over time</h2>
          <span className="gap" />
          <span className="rt">{windowLabel}</span>
        </div>
        {chips}
        <div className="nw">
          <div className="hd">
            <span aria-hidden="true" className="material-icons">timeline</span>
            {why?.reason === 'loading' ? 'Reading the last pull…' : 'No series to plot'}
          </div>
          {why ? why.needs : 'No daily series is available.'}
          {why?.reason === 'not enough daily data' ? (
            <>
              {' '}
              The days come from the last metrics pull&apos;s artifact, so a wider
              pull window (or simply more delivery) is what lengthens this.
            </>
          ) : null}
        </div>
      </div>
    );
  }

  // Google Ads and DV360 pulls emit per-day rows; LinkedIn and Meta metric pulls
  // do not. So on a mixed account the chart is a TRUE read of FEWER campaigns
  // than the tiles above it. Saying so is the difference between a narrower
  // chart and a wrong one.
  const partial = coverage !== null && totalCampaigns !== null
    && coverage > 0 && coverage < totalCampaigns;

  return (
    <div className="card">
      <div className="ch">
        <h2>Performance over time</h2>
        <span className="gap" />
        {/* The design's key, with the REAL period length substituted in. It
            said "solid = last 30d · dashed = previous 30d"; a halved pull
            window is usually 15 and 15, and claiming 30 would misstate the
            comparison by a factor of two. With the comparison off there is one
            line, so the key just names the window it covers. */}
        <span className="rt">
          {compared
            ? `solid = last ${daysPerPeriod}d · dashed = previous ${daysPerPeriod}d`
            : latestDayExcluded
              ? `${daysPerPeriod || data.length} complete days`
              : `${daysPerPeriod || data.length} days from the last pull`}
        </span>
      </div>
      {chips}
      <Suspense fallback={<div className="chartwrap" />}>
        <TrendPlot
          data={data}
          format={active.format}
          currency={currency}
          showPrevious={compared}
        />
      </Suspense>
      {/* Two series get a legend; one does not — the heading and the key above
          already name it, and a legend box for a single line is noise. The
          total still earns its place, so it stays either way. */}
      <div className="lgd">
        <span>
          <i style={{ background: CURRENT_COLOR }} />
          {compared ? 'This period' : `${active.label} total`}
          {' '}
          {fmt(totalsOf('current'), active.format, currency)}
        </span>
        {compared ? (
          <span>
            <i style={{ background: PRIOR_COLOR }} />
            Prior period
            {' '}
            {fmt(totalsOf('previous'), active.format, currency)}
          </span>
        ) : null}
      </div>
      {latestDayExcluded ? (
        <div className="cap">
          {`${latestDayExcluded} is excluded because the newest reported day may be incomplete.`}
        </div>
      ) : null}
      {partial ? (
        <div className="cap">
          <b>
            {`${coverage} of ${totalCampaigns} campaigns`}
          </b>
          {' — Google Ads and DV360 pulls report per-day rows; LinkedIn and Meta '}
          {'metric pulls do not, so those campaigns are absent from this chart even '}
          {'though they are counted in the tiles above. A DV360 pull from before '}
          {'per-day rows were added also has none until it is re-pulled'}
          {newestPullAt ? ` (newest pull: ${new Date(newestPullAt).toISOString()})` : ''}
          {'.'}
        </div>
      ) : null}
    </div>
  );
}
